import React from 'react';

import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

type BranchItem = {
  label: string;
  sub?: string;
};

type OutputNode = {
  label: string;
  sub?: string;
  highlight?: boolean;
  branches?: BranchItem[];
};

type Props = {
  input: {
    label: string;
    sub?: string;
    highlight?: boolean;
  };
  outputs: OutputNode[];
  /** trueの場合、出力ノードを上に、入力ノード（結果）を下に配置する（複数入力 → 1出力の表現用） */
  reverse?: boolean;
};

/**
 * 入力ノードから複数の出力ノードに分岐するフロー図
 * reverse=true の場合は複数ノード → 1つの結果ノードとして表示
 */
const WorkflowBranchFlow: React.FC<Props> = ({ input, outputs, reverse }) => {
  const inputNode = (
    <Box
      sx={{
        border: input.highlight ? 2 : 1,
        borderColor: input.highlight ? 'primary.main' : 'divider',
        borderRadius: 2,
        px: 3,
        py: 1.5,
        textAlign: 'center',
        minWidth: 240,
      }}
    >
      <Typography
        variant="body2"
        fontWeight={600}
        color={input.highlight ? 'primary.main' : 'text.primary'}
      >
        {input.label}
      </Typography>
      {input.sub && (
        <Typography variant="caption" color="text.secondary">
          {input.sub}
        </Typography>
      )}
    </Box>
  );

  const outputNodes = (
    <Stack direction="row" spacing={2} justifyContent="center" sx={{ flexWrap: 'wrap', gap: 1 }}>
      {outputs.map((output) => (
        <Stack key={output.label} alignItems="center" spacing={0.5}>
          <Box
            sx={{
              border: output.highlight ? 2 : 1,
              borderColor: output.highlight ? 'primary.main' : 'divider',
              borderRadius: 2,
              px: 2.5,
              py: 1.5,
              textAlign: 'center',
              minWidth: 180,
            }}
          >
            <Typography
              variant="body2"
              fontWeight={600}
              color={output.highlight ? 'primary.main' : 'text.primary'}
            >
              {output.label}
            </Typography>
            {output.sub && (
              <Typography variant="caption" color="text.secondary">
                {output.sub}
              </Typography>
            )}
          </Box>
          {/* ブランチ（子要素） */}
          {output.branches && output.branches.length > 0 && (
            <>
              <ArrowDownwardIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
              <Stack spacing={0.5}>
                {output.branches.map((branch) => (
                  <Stack
                    key={branch.label}
                    direction="row"
                    alignItems="center"
                    spacing={0.75}
                    justifyContent="center"
                  >
                    <Chip
                      label={branch.label}
                      size="small"
                      variant="outlined"
                      sx={{ fontWeight: 600 }}
                    />
                    {branch.sub && (
                      <>
                        <Typography variant="caption" color="text.disabled">
                          →
                        </Typography>
                        <Chip
                          label={branch.sub}
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{ fontWeight: 600 }}
                        />
                      </>
                    )}
                  </Stack>
                ))}
              </Stack>
            </>
          )}
        </Stack>
      ))}
    </Stack>
  );

  if (reverse) {
    return (
      <Stack alignItems="center" spacing={0.5} sx={{ py: 1 }}>
        {outputNodes}
        <ArrowDownwardIcon sx={{ fontSize: 20, color: 'text.disabled' }} />
        {inputNode}
      </Stack>
    );
  }

  return (
    <Stack alignItems="center" spacing={0.5} sx={{ py: 1 }}>
      {inputNode}
      <ArrowDownwardIcon sx={{ fontSize: 20, color: 'text.disabled' }} />
      {outputNodes}
    </Stack>
  );
};

export default WorkflowBranchFlow;
